/**
 * Como cada valor de enum de compras se lê na tela.
 *
 * Papel: aqui ficam os seis mapas que traduzem etapa do funil, canal,
 * categoria, forma de pagamento, situação financeira e intervalo de parcelas
 * para o texto que aparece no quadro, na tabela de finalizados e no formulário,
 * e `ROTULO_SEM_RESIDENCIA`, o texto do pedido que não é de casa nenhuma.
 *
 * Como usar: um valor novo em qualquer um dos enums do contrato precisa de uma
 * linha aqui. Os mapas são `Record` do tipo do enum, e não objetos soltos, para
 * que acrescentar o valor lá e esquecer aqui vire erro de typecheck em vez de
 * uma coluna sem título no quadro ou um `Chip` sem texto no formulário.
 *
 * Duas etapas têm rótulo de duas fases, e em caixa alta — `comprado` e
 * `entregue` —, porque cada uma cobre dois momentos que a pessoa distingue e o
 * funil não: comprado já pode estar a caminho, e entregue ainda pode aguardar a
 * confirmação de quem recebeu. O mesmo texto serve ao cabeçalho da coluna do
 * quadro e ao `Chip` de etapa do formulário, e por isso não existe forma curta.
 */
import {
  type CanalOrigemPedido,
  type CategoriaPedidoCompra,
  type FormaPagamentoPedido,
  type IntervaloParcelamento,
  type StatusFinanceiroPedido,
  type StatusPedidoCompra,
} from '@concierge/shared';

/**
 * O texto do pedido com `residenciaId` nulo, no lugar do nome da casa.
 *
 * É o mesmo no `Chip` de RESIDÊNCIA do formulário, no cartão do quadro e na
 * coluna Residência dos finalizados, onde a busca e a ordenação também o leem:
 * quem procurar por ele na busca encontra os pedidos sem casa, e quem ordenar
 * pela coluna os vê entre as casas pela letra deste texto.
 */
export const ROTULO_SEM_RESIDENCIA = 'Ambas / não se aplica';

/** A etapa do funil, como aparece na coluna do quadro e no `Chip` do formulário. */
export const ROTULOS_STATUS: Record<StatusPedidoCompra, string> = {
  em_cotacao: 'Em Cotação',
  em_aprovacao: 'Em Aprovação',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
  em_processo_de_compra: 'Em Processo de Compra',
  comprado: 'COMPRADO | A CAMINHO',
  entregue: 'ENTREGUE | AGUARDANDO CONFIRMAÇÃO',
  finalizado: 'Finalizado',
};

/** Por onde o pedido chegou. */
export const ROTULOS_CANAL: Record<CanalOrigemPedido, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  presencial: 'Presencial',
};

/** A natureza do que se está comprando. */
export const ROTULOS_CATEGORIA: Record<CategoriaPedidoCompra, string> = {
  manutencao: 'Manutenção',
  decoracao: 'Decoração',
  uniformes: 'Uniformes',
  limpeza_e_insumos: 'Limpeza e Insumos',
  equipamentos: 'Equipamentos',
  alimentos_e_bebidas: 'Alimentos e Bebidas',
  outros: 'Outros',
};

/** Como o item foi pago. */
export const ROTULOS_PAGAMENTO: Record<FormaPagamentoPedido, string> = {
  cartao_de_credito: 'Cartão de crédito',
  pix: 'Pix',
  boleto: 'Boleto',
  dinheiro: 'Dinheiro',
  outro: 'Outro',
};

/** Se o item já saiu do caixa. */
export const ROTULOS_STATUS_FINANCEIRO: Record<StatusFinanceiroPedido, string> = {
  pago: 'Pago',
  provisionado: 'Provisionado',
};

/**
 * De quanto em quanto tempo as parcelas de um item vencem.
 *
 * `Datas avulsas` gera todas as parcelas na mesma data da compra, e é o ponto de
 * partida para quem vai ajustar cada vencimento depois na tela de finanças — o
 * item de pedido não tem campo por parcela.
 */
export const ROTULOS_INTERVALO: Record<IntervaloParcelamento, string> = {
  mensal: 'Mensal',
  quinzenal: 'Quinzenal (15 dias)',
  semanal: 'Semanal (7 dias)',
  avulso: 'Datas avulsas',
};
